import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, CalendarDays, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, addDays, subDays } from 'date-fns';
import AppointmentCalendarView from '@/components/admin/AppointmentCalendarView';
import CompleteAppointmentDialog from '@/components/admin/CompleteAppointmentDialog';
import RescheduleAppointmentDialog from '@/components/admin/RescheduleAppointmentDialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { TIMEZONE_LABEL } from '@/lib/timezoneConfig';

type ViewMode = 'day' | 'tomorrow' | 'week';

export default function SchedulingCalendar() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);

  const { data: appointments = [], refetch } = useQuery({
    queryKey: ['calendar-appointments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .neq('status', 'cancelled')
        .order('preferred_date', { ascending: true })
        .order('preferred_time', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel('calendar-appointments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  const handlePrevious = () => {
    if (viewMode === 'day') {
      setSelectedDate(subDays(selectedDate, 1));
    } else if (viewMode === 'tomorrow') {
      setSelectedDate(subDays(selectedDate, 1));
    } else {
      setSelectedDate(subDays(selectedDate, 7));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, 1));
    } else if (viewMode === 'tomorrow') {
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      setSelectedDate(addDays(selectedDate, 7));
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
    setViewMode('day');
  };

  const handleViewAppointment = (appointment: any) => {
    setSelectedAppointment(appointment);
    setShowViewDialog(true);
  };

  const handleCompleteAppointment = (appointment: any) => {
    setSelectedAppointment(appointment);
    setShowCompleteDialog(true);
  };

  const handleRescheduleAppointment = (appointment: any) => {
    setSelectedAppointment(appointment);
    setShowRescheduleDialog(true);
  };

  const getStatusBadge = (status: string, completedAt?: string, requiresApproval?: boolean) => {
    if (completedAt || status === 'completed') {
      return <Badge className="bg-green-500">Completed</Badge>;
    }
    if (status === 'confirmed') return <Badge className="bg-blue-500">Confirmed</Badge>;
    if (status === 'pending' && requiresApproval) return <Badge className="bg-orange-500">Pending Approval</Badge>;
    if (status === 'pending') return <Badge variant="secondary">Pending</Badge>;
    if (status === 'cancelled') return <Badge variant="destructive">Cancelled</Badge>;
    if (status === 'pending_approval') return <Badge className="bg-orange-500">Pending Approval</Badge>;
    if (status === 'modification_requested') return <Badge className="bg-yellow-500">Modification Requested</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">Appointment Calendar</h1>
            <p className="text-muted-foreground flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Visual schedule and quick completion tracking • All times in {TIMEZONE_LABEL}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline" size="icon" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon">
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                      />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <Card className="p-2">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="day">
                <CalendarDays className="h-4 w-4 mr-2" />
                Today
              </TabsTrigger>
              <TabsTrigger value="tomorrow">
                <CalendarDays className="h-4 w-4 mr-2" />
                Tomorrow
              </TabsTrigger>
              <TabsTrigger value="week">
                <CalendarDays className="h-4 w-4 mr-2" />
                Week
              </TabsTrigger>
            </TabsList>

            <div className="p-4">
              <AppointmentCalendarView
                appointments={appointments}
                viewMode={viewMode}
                selectedDate={selectedDate}
                onViewAppointment={handleViewAppointment}
                onCompleteAppointment={handleCompleteAppointment}
                onRescheduleAppointment={handleRescheduleAppointment}
              />
            </div>
          </Tabs>
        </Card>

        {/* Legend */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Status Color Legend</h3>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500/10 border-2 border-green-500/20"></div>
              <span className="text-sm">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500/10 border-2 border-blue-500/20"></div>
              <span className="text-sm">Confirmed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-500/10 border-2 border-gray-500/20"></div>
              <span className="text-sm">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-500/10 border-2 border-orange-500/20"></div>
              <span className="text-sm">Pending Approval</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500/10 border-2 border-yellow-500/20"></div>
              <span className="text-sm">Modification Requested</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500/10 border-2 border-red-500/20"></div>
              <span className="text-sm">Cancelled</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Complete Appointment Dialog */}
      <CompleteAppointmentDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
        appointment={selectedAppointment}
        onComplete={() => {
          refetch();
          setSelectedAppointment(null);
        }}
      />

      {/* Reschedule Appointment Dialog */}
      <RescheduleAppointmentDialog
        open={showRescheduleDialog}
        onOpenChange={setShowRescheduleDialog}
        appointment={selectedAppointment}
        onReschedule={() => {
          refetch();
          setSelectedAppointment(null);
        }}
      />

      {/* View Appointment Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
            <DialogDescription>View appointment information</DialogDescription>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-4">
              <div>
                <span className="font-semibold">Client:</span> {selectedAppointment.name}
              </div>
              <div>
                <span className="font-semibold">Email:</span> {selectedAppointment.email}
              </div>
              <div>
                <span className="font-semibold">Phone:</span> {selectedAppointment.phone || 'N/A'}
              </div>
              <div>
                <span className="font-semibold">Service:</span> {selectedAppointment.service_type}
              </div>
              <div>
                <span className="font-semibold">Type:</span> {selectedAppointment.appointment_type || 'N/A'}
              </div>
              <div>
                <span className="font-semibold">Date:</span>{' '}
                {selectedAppointment.preferred_date
                  ? format(new Date(selectedAppointment.preferred_date), 'PPP')
                  : 'N/A'}
              </div>
              <div>
                <span className="font-semibold">Time:</span> {selectedAppointment.preferred_time || 'N/A'}
              </div>
              <div>
                <span className="font-semibold">Status:</span>{' '}
                {getStatusBadge(selectedAppointment.status, selectedAppointment.completed_at, selectedAppointment.requires_approval)}
              </div>
              {selectedAppointment.message && (
                <div>
                  <span className="font-semibold">Message:</span>
                  <p className="text-sm text-muted-foreground mt-1">{selectedAppointment.message}</p>
                </div>
              )}
              {selectedAppointment.completion_notes && (
                <div>
                  <span className="font-semibold">Completion Notes:</span>
                  <p className="text-sm text-muted-foreground mt-1">{selectedAppointment.completion_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
